import { URL_ACCOUNT_SELECT } from '@seek/adv-constants';
import { useHirerAuth } from '@seek/hirer-auth-react';
import { useAccountContext } from '@seek/hirer-graphql-react';
import { useTranslations } from '@vocab/react';
import { Alert, Box, Loader, Stack } from 'braid-design-system';
import { useEffect } from 'react';

import { UserLoginPrompt } from 'src/components/UserLoginPrompt/UserLoginPrompt';
import type { Partner } from 'src/types';
import { useIntegrationPartners } from 'src/utils/hooks/useIntegrationPartners';
import { makePartnerImageUrl } from 'src/utils/partners/makePartnerImageUrl';

import { Form } from './components/Form/Form';

import translations from 'src/pages/LinkYourSoftware/.vocab';

export const Request = () => {
  const { selectedPartner } = useIntegrationPartners();

  return (
    <>
      {selectedPartner && (
        <Stack space="xlarge">
          <>
            {selectedPartner.imageUrl && (
              <Box paddingBottom="gutter">
                <img
                  src={`${makePartnerImageUrl(
                    selectedPartner.releaseNotesImageUrl ||
                      selectedPartner.imageUrl,
                  )}`}
                  alt={`${selectedPartner.name} logo`}
                  style={{
                    width: 'auto',
                    maxHeight: '90px',
                    maxWidth: '100%',
                  }}
                />
              </Box>
            )}
            <RequestLoggedInSection partner={selectedPartner} />
          </>
        </Stack>
      )}
    </>
  );
};

const RequestLoggedInSection = ({
  partner: selectedPartner,
}: {
  partner: Partner;
}) => {
  const { authenticated } = useHirerAuth();
  const { accountContext, loading, error } = useAccountContext();

  useEffect(() => {
    // If the user is authenticated but hasn't selected an account, redirect them to the account selection page.
    // The forms (& much common tooling) won't work without.

    if (
      authenticated &&
      !loading &&
      !error &&
      !accountContext?.currentAdvertiser
    ) {
      window.location.assign(
        `${URL_ACCOUNT_SELECT}?returnUrl=${encodeURIComponent(
          window.location.pathname + window.location.search,
        )}`,
      );
    }
  }, [authenticated, accountContext, loading, error]);

  const { t } = useTranslations(translations);

  if (!authenticated || !accountContext?.currentAdvertiser) {
    return <UserLoginPrompt />;
  }

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <Alert tone="critical">{t('somethingWentWrong')}</Alert>;
  }

  return <Form partner={selectedPartner} accountContext={accountContext} />;
};
